import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookie from 'js-cookie';
//icons
import { FaEye, FaEyeSlash } from 'react-icons/fa';
//api
import { registerUser } from 'api/users';
import { addItemCart } from 'api/ecommerce';
import { buildAddItemCartPayload } from 'utils/bookCondition';
import { getAllBookStores } from 'api/company';
import { loginApi } from 'api/login';
//stores
import { useUserStore } from 'stores/userStore';
import { useCartStore } from 'stores/cartStore';
import { useCompanyStore } from 'stores/companyStore';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//components
import { CustomSelectComponent } from 'components/CustomSelectComponent';
import Button from 'components/userzone/buttons/ButtonCustom';
import FeedbackStatus from 'components/userzone/FeedbackStatus';

const RegisterComp = () => {
  const { t } = useTranslation(['register', 'backoffice']);
  const [receiveInvoices, setReceiveInvoices] = useState(false);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [receiveNewsOffers, setReceiveNewsOffers] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [backendErrors, setBackendErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [msg, setMsg] = useState('');
  const { loginSuccess, setBenefitedBookstore, fetchUserInfo } = useUserStore();
  const { assignedLibrary } = useAssignedLibrary();
  const { is_single_company: isMonoCliente } = useCompanyStore();
  const [librarySearch, setLibrarySearch] = useState('');

  const {
    formState: { errors },
    control,
    handleSubmit,
    watch,
    setError,
    setValue,
  } = useForm();

  const navigate = useNavigate();
  const { items } = useCartStore();

  const togglePasswordVisibility = (field, setField) => {
    setField(!field);
  };

  useEffect(() => {
    const fetchBookStores = async () => {
      setIsLoading(true);
      try {
        const response = await getAllBookStores({ search: librarySearch });
        const stores = response.data.results;
        const bookStores = stores.map((store) => ({
          value: store.id,
          label: store.name,
          subtitle: store.direccion,
        }));
        if (isMonoCliente && stores.length === 1) {
          const onlyStore = bookStores[0];
          setOptions([onlyStore]);
          if (setValue) {
            setValue('benefited_bookstore', onlyStore);
          }
        } else {
          setOptions(bookStores);
        }
      } catch (error) {
        console.error('Error fetching book stores:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if(librarySearch.length > 1) fetchBookStores();
  }, [isMonoCliente, setValue, librarySearch]);

  useEffect(() => {
    if (backendErrors) {
      Object.entries(backendErrors).forEach(([field, message]) => {
        setError(field, { type: 'manual', message });
      });
    }
  }, [backendErrors, errors]);

  const passwordWatch = watch('password');

  const handleRegisterUser = async (values) => {
    const payload = {
      first_name: values?.name,
      email: values?.email,
      username: values.email,
      last_name: values.surname,
      password: values.password,
      password2: values.password2,
      date_birth: values.date_birth,
      receive_invoices: receiveInvoices,
      receive_news_offers: receiveNewsOffers,
      accept_conditions: acceptConditions,
      active: true,
      benefited_bookstore: values.benefited_bookstore,
      type: 'normal',
    };
    try {
      const { status, data } = await registerUser(payload);
      if (status === 201) {
        const payload = { username: values.email, password: values.password };
        const { status: statusAuth, data: dataAuth } = await loginApi(payload);
        if (statusAuth === 200) {
          localStorage.setItem('access', dataAuth.access);
          localStorage.setItem('refresh', dataAuth.refresh);
          localStorage.setItem('type_user', dataAuth.type_user);
          localStorage.setItem('permissions', dataAuth.permissions);
          Cookie.set('access', dataAuth.access, { sameSite: 'strict' });
          Cookie.set('refresh', dataAuth.refresh, { sameSite: 'strict' });
          Cookie.set('type_user', dataAuth.type_user, { sameSite: 'strict' });
          Cookie.set('permissions', dataAuth.permissions, { sameSite: 'strict' });
          fetchUserInfo();
          loginSuccess();
        }

        setBenefitedBookstore({
          value: data?.benefited_bookstore?.id,
          label: data?.benefited_bookstore?.name,
          subtitle: data?.benefited_bookstore?.direccion,
          url: data?.benefited_bookstore?.url,
        });

        for (const item of items) {
          await addItemCart(buildAddItemCartPayload(item.id, item.quantity, item, item.book_condition));
        }

        navigate('/', { replace: true });
      } else if (status === 400 && data) {
        setBackendErrors(data);
        setFeedbackType('error');
        setShowCard(true);
        setMsg(data);
      } else {
        setFeedbackType('error');
        setShowCard(true);
        setMsg(t('somethingwrong'));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Form onSubmit={handleSubmit(handleRegisterUser)}>
      <div className="row">
        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formName">
            <Form.Label className="text-dark label-form">{t('name')} </Form.Label>
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('backoffice:camporequerido'),
                },
              }}
              render={({ field }) => (
                <>
                  <Form.Control type="text" {...field} />
                  {errors.name && <small className="text-danger ">{errors.name.message}</small>}
                </>
              )}
            />
          </Form.Group>
        </div>
        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formLastname">
            <Form.Label className="text-dark label-form">{t('lastname')}</Form.Label>
            <Controller
              name="surname"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('backoffice:camporequerido'),
                },
              }}
              render={({ field }) => (
                <>
                  <Form.Control type="text" {...field} />
                  {errors.surname && <small className="text-danger ">{errors.surname.message}</small>}
                </>
              )}
            />
          </Form.Group>
        </div>

        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formEmail">
            <Form.Label className="text-dark label-form">{t('email')}</Form.Label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('backoffice:camporequerido'),
                },
              }}
              render={({ field }) => (
                <>
                  <Form.Control type="text" {...field} />
                  {errors.email && <small className="text-danger ">{errors.email.message}</small>}
                </>
              )}
            />
          </Form.Group>
        </div>

        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formPassword">
            <Form.Label className="text-dark label-form">{t('password')} </Form.Label>
            <Controller
              name="password"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('backoffice:camporequerido'),
                },
              }}
              render={({ field }) => (
                <div className="input-group">
                  <Form.Control type={showPassword ? 'text' : 'password'} {...field} />
                  <span
                    className="password-register"
                    onClick={() => togglePasswordVisibility(showPassword, setShowPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              )}
            />
            {errors.password && <small className="text-danger">{errors.password.message}</small>}
          </Form.Group>
        </div>

        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formPassword2">
            <Form.Label className="text-dark label-form">{t('repeatpassword')} </Form.Label>
            <Controller
              name="password2"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('backoffice:camporequerido'),
                },
                validate: (value) => value === passwordWatch || t('passworddontmatch'),
              }}
              render={({ field }) => (
                <div className="input-group">
                  <Form.Control type={showRepeatPassword ? 'text' : 'password'} {...field} />
                  <span
                    className="password-register"
                    onClick={() => togglePasswordVisibility(showRepeatPassword, setShowRepeatPassword)}
                  >
                    {showRepeatPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              )}
            />
            {errors.password2 && <small className="text-danger">{errors.password2.message}</small>}
          </Form.Group>
        </div>

        <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
          <Form.Group controlId="formDateBirth">
            <Form.Label className="text-dark">{t('date_birth', { ns: 'backoffice' })}</Form.Label>
            <Controller
              name="date_birth"
              control={control}
              render={({ field }) => (
                <>
                  <Form.Control type="date" {...field} />
                  {errors.date_birth && <small className="text-danger">{errors.date_birth.message}</small>}
                </>
              )}
            />
          </Form.Group>
        </div>

        {!isMonoCliente && (
          <div className="col-lg-4 col-12 col-md-6 px-2 mb-3">
            <Form.Group controlId="formLibrary">
              <Form.Label className="text-dark label-form">{t('benefitedbookstore')}</Form.Label>
              <Controller
                name="benefited_bookstore"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('backoffice:camporequerido'),
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <CustomSelectComponent
                    handleSelection={onChange}
                    options={options}
                    selectedOptionDefault={value}
                    isDisabled={isMonoCliente}
                    onInputChange={setLibrarySearch}
                  />
                )}
              />
              {errors?.benefited_bookstore && (
                <small className="text-danger ">{errors.benefited_bookstore.message}</small>
              )}
            </Form.Group>
          </div>
        )}
      </div>

      <div className="mt-4 d-flex checkDiv">
        <Form.Check
          type={'checkbox'}
          className="mx-3"
          onChange={(e) => setReceiveInvoices(!receiveInvoices)}
          checked={receiveInvoices}
        />
        <p className="m-0">{t('wantgetbills')}</p>
      </div>
      <div className="mt-4 d-flex checkDiv">
        <Form.Check
          type={'checkbox'}
          className="mx-3"
          onChange={(e) => setReceiveNewsOffers(!receiveNewsOffers)}
          checked={receiveNewsOffers}
        />
        <p>{t('wantreceivenews')}</p>
      </div>
      <div className="mt-1 d-flex checkDiv">
        <Form.Check
          type={'checkbox'}
          className="mx-3"
          onChange={(e) => setAcceptConditions(!acceptConditions)}
          checked={acceptConditions}
        />
        <p>
          {t('readandunderstand')}
          <Link to={`/${assignedLibrary}/aviso-legal-y-politica-de-privacidad`}> {t('legalnotice')}</Link> {t('andthe')}
          <Link to={`/${assignedLibrary}/condiciones-de-uso`}> {t('termsuse')}</Link>
        </p>
      </div>
      {feedbackType && <FeedbackStatus feedbackType={feedbackType} isVisible={showCard} msgObject={msg} />}
      <Button
        variant="primary"
        extraClass="mt-3 mx-3 mb-3"
        type="submit"
        disabled={!acceptConditions}
        onClick={handleSubmit(handleRegisterUser)}
      >
        {t('register')}
      </Button>
    </Form>
  );
};
export default RegisterComp;
